// File Metadata:
// Filename: chatDb.js
// Version: 1.00
// Created: 2025-04-14 21:14:42
// Last Modified: 2025-04-16 19:53:57
// Authors: [Your Name]
// Description: A brief summary of what this file does

/**
 * Chat Database Service
 * 
 * Provides IndexedDB storage for chat rooms and messages, enabling offline
 * support and message persistence when navigating between pages.
 * 
 * @version 1.0.0
 * @created 2025-04-14
 */

import { openDB } from 'idb';

// Database version - increment when schema changes
const DB_VERSION = 2; // Increased version for schema update
const DB_NAME = 'familysync-chat-db';

// IndexedDB schema
const STORES = {
  ROOMS: 'rooms',
  MESSAGES: 'messages',
  OFFLINE_QUEUE: 'offlineQueue',
  UNREAD_COUNTS: 'unreadCounts' // New store for unread counts
};

// Initialize and get database connection
const getDb = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      // Create rooms store
      if (!db.objectStoreNames.contains(STORES.ROOMS)) {
        const roomsStore = db.createObjectStore(STORES.ROOMS, { keyPath: 'roomId' });
        roomsStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
      }
      
      // Create messages store
      if (!db.objectStoreNames.contains(STORES.MESSAGES)) {
        const messagesStore = db.createObjectStore(STORES.MESSAGES, { keyPath: '_id' });
        messagesStore.createIndex('room', 'room', { unique: false });
        messagesStore.createIndex('timestamp', 'timestamp', { unique: false });
        messagesStore.createIndex('roomAndTime', ['room', 'timestamp'], { unique: false });
        messagesStore.createIndex('isPending', 'isPending', { unique: false });
      }
      
      // Create offline queue store
      if (!db.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const queueStore = db.createObjectStore(STORES.OFFLINE_QUEUE, { 
          keyPath: 'id',
          autoIncrement: true 
        });
        queueStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      // Add unread counts store for version 2
      if (oldVersion < 2 && !db.objectStoreNames.contains(STORES.UNREAD_COUNTS)) {
        db.createObjectStore(STORES.UNREAD_COUNTS, { keyPath: 'roomId' });
      }
    }
  });
};

// === ROOMS OPERATIONS ===

/**
 * Save a single chat room to IndexedDB
 * @param {Object} room - The room object to save
 * @returns {boolean} - Whether the save was successful
 */
export const saveRoom = async (room) => {
  if (!room) return false;
  
  // Ensure room has a valid roomId
  const roomToSave = { ...room };
  
  // If roomId is missing but id or _id exists, use that as roomId
  if (!roomToSave.roomId && (roomToSave.id || roomToSave._id)) {
    roomToSave.roomId = roomToSave._id || roomToSave.id;
  }
  
  // If we still don't have a roomId, we can't save the room
  if (!roomToSave.roomId) {
    console.error('Cannot save room without roomId:', room);
    return false;
  }
  
  const db = await getDb();
  await db.put(STORES.ROOMS, {
    ...roomToSave,
    lastUpdated: new Date().toISOString()
  });
  
  return true;
};

/**
 * Save multiple chat rooms to IndexedDB
 * @param {Array<Object>} rooms - Array of room objects to save
 */
export const saveRooms = async (rooms) => {
  if (!Array.isArray(rooms) || rooms.length === 0) return;
  
  const db = await getDb();
  const tx = db.transaction(STORES.ROOMS, 'readwrite');
  
  const lastUpdated = new Date().toISOString();
  
  // Filter out any invalid room objects (missing roomId)
  const validRooms = rooms.filter(room => room && room.roomId);
  
  // Handle case where room has an id or _id but not roomId
  const processedRooms = validRooms.map(room => {
    // If roomId is missing but id or _id exists, use that as roomId
    if (!room.roomId && (room.id || room._id)) {
      return {
        ...room,
        roomId: room.roomId || room._id || room.id,
        lastUpdated
      };
    }
    return {
      ...room,
      lastUpdated
    };
  });
  
  // Only put valid rooms into the store
  await Promise.all([
    ...processedRooms.map(room => tx.store.put(room)),
    tx.done
  ]);
};

/**
 * Get all chat rooms from IndexedDB
 * @param {number} limit - Maximum number of rooms to retrieve
 * @returns {Array<Object>} Array of room objects
 */
export const getRooms = async (limit = 100) => {
  const db = await getDb();
  
  // Get rooms ordered by last update time (most recent first)
  return db.getAllFromIndex(
    STORES.ROOMS, 
    'lastUpdated', 
    undefined, 
    limit
  );
};

/**
 * Get a specific room by ID
 * @param {string} roomId - ID of the room to retrieve
 * @returns {Object|undefined} Room object or undefined if not found
 */
export const getRoom = async (roomId) => {
  const db = await getDb();
  return db.get(STORES.ROOMS, roomId);
};

// === UNREAD COUNTS OPERATIONS ===

/**
 * Save unread counts for all rooms
 * @param {Object} unreadCountByRoom - Object mapping roomIds to unread counts
 */
export const saveUnreadCounts = async (unreadCountByRoom) => {
  if (!unreadCountByRoom || typeof unreadCountByRoom !== 'object') return;
  
  const db = await getDb();
  const tx = db.transaction(STORES.UNREAD_COUNTS, 'readwrite');
  
  const promises = Object.entries(unreadCountByRoom).map(([roomId, count]) => {
    return tx.store.put({
      roomId,
      count,
      lastUpdated: new Date().toISOString()
    });
  });
  
  await Promise.all([...promises, tx.done]);
};

/**
 * Save unread count for a specific room
 * @param {string} roomId - The room ID
 * @param {number} count - The unread message count
 */
export const saveUnreadCount = async (roomId, count) => {
  if (!roomId) return;
  
  const db = await getDb();
  await db.put(STORES.UNREAD_COUNTS, {
    roomId,
    count,
    lastUpdated: new Date().toISOString()
  });
};

/**
 * Get unread counts for all rooms
 * @returns {Object} Object mapping roomIds to unread counts
 */
export const getUnreadCounts = async () => {
  const db = await getDb();
  const counts = await db.getAll(STORES.UNREAD_COUNTS);
  
  return counts.reduce((acc, item) => {
    acc[item.roomId] = item.count;
    return acc;
  }, {});
};

/**
 * Get unread count for a specific room
 * @param {string} roomId - The room ID
 * @returns {number} The unread count or 0 if not found
 */
export const getUnreadCount = async (roomId) => {
  if (!roomId) return 0;
  
  const db = await getDb();
  const data = await db.get(STORES.UNREAD_COUNTS, roomId);
  return data ? data.count : 0;
};

// === MESSAGES OPERATIONS ===

/**
 * Save a single message to IndexedDB
 * @param {Object} message - The message to save
 */
export const saveMessage = async (message) => {
  if (!message || !message._id || !message.room) {
    console.error('Invalid message object:', message);
    return;
  }
  
  const db = await getDb();
  await db.put(STORES.MESSAGES, message);
};

/**
 * Save multiple messages to IndexedDB
 * @param {Array<Object>} messages - Array of message objects to save
 */
export const saveMessages = async (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return;
  
  const db = await getDb();
  const tx = db.transaction(STORES.MESSAGES, 'readwrite');
  
  await Promise.all([
    ...messages.map(message => {
      if (message && message._id && message.room) {
        return tx.store.put(message);
      }
      return Promise.resolve();
    }),
    tx.done
  ]);
};

/**
 * Get messages for a specific chat room
 * @param {string} roomId - ID of the room to get messages for
 * @param {number} limit - Maximum number of messages to retrieve
 * @param {boolean} newestFirst - Whether to sort by newest first
 * @returns {Array<Object>} Array of message objects
 */
export const getMessagesByRoom = async (roomId, limit = 50, newestFirst = true) => {
  if (!roomId) {
    console.error('Invalid roomId provided to getMessagesByRoom:', roomId);
    return [];
  }

  try {
    const db = await getDb();
    
    // Try to use index directly first
    const tx = db.transaction(STORES.MESSAGES, 'readonly');
    const roomIndex = tx.store.index('room');
    
    // Use basic index query instead of compound index
    const messages = await roomIndex.getAll(roomId);
    
    // Sort in memory instead of using the compound index
    const sortedMessages = messages.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return newestFirst ? timeB - timeA : timeA - timeB;
    });
    
    // Limit the result
    return sortedMessages.slice(0, limit);
  } catch (error) {
    console.error('Error fetching messages by room:', error);
    return [];
  }
};

/**
 * Delete old messages (retention policy)
 * @param {number} days - Number of days to keep messages for
 */
export const deleteOldMessages = async (days = 30) => {
  const db = await getDb();
  const tx = db.transaction(STORES.MESSAGES, 'readwrite');
  const index = tx.store.index('timestamp');
  
  // Calculate cutoff date
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  const cutoffTimestamp = cutoffDate.toISOString();
  
  // Create range for old messages
  const range = IDBKeyRange.upperBound(cutoffTimestamp);
  
  // Delete all messages older than the cutoff
  let cursor = await index.openCursor(range);
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  
  await tx.done;
};

// === OFFLINE QUEUE OPERATIONS ===

/**
 * Queue a message for sending when back online
 * @param {Object} message - The message to queue
 */
export const queueOfflineMessage = async ({ roomId, content, type = 'text' }) => {
  const db = await getDb();
  
  await db.add(STORES.OFFLINE_QUEUE, {
    roomId,
    content,
    type,
    timestamp: new Date().toISOString()
  });
};

/**
 * Get all queued offline messages
 * @returns {Array<Object>} Array of queued message objects
 */
export const getOfflineMessages = async () => {
  const db = await getDb();
  return db.getAllFromIndex(STORES.OFFLINE_QUEUE, 'timestamp');
};

/**
 * Remove a message from the offline queue
 * @param {number} id - The ID of the queued message to remove
 */
export const removeOfflineMessage = async (id) => {
  const db = await getDb();
  await db.delete(STORES.OFFLINE_QUEUE, id);
};

/**
 * Clear all messages from the offline queue
 */
export const clearOfflineQueue = async () => {
  const db = await getDb();
  const tx = db.transaction(STORES.OFFLINE_QUEUE, 'readwrite');
  await tx.store.clear();
  await tx.done;
};

// === UTILITY FUNCTIONS ===

/**
 * Initialize chat database and handle migrations if needed
 */
export const initChatDb = async () => {
  try {
    await getDb();
    console.log('Chat database initialized successfully');
    
    // Log WebSocket connection mode to help with HTTPS debugging
    const isSecureContext = window.isSecureContext;
    const protocol = window.location.protocol;
    console.log(`WebSocket environment: Protocol: ${protocol}, Secure Context: ${isSecureContext}, Expected WebSocket type: ${protocol === 'https:' ? 'wss://' : 'ws://'}`);
    
    // Apply retention policy in the background
    deleteOldMessages().catch(err => {
      console.error('Error applying message retention policy:', err);
    });
    
    return true;
  } catch (error) {
    console.error('Failed to initialize chat database:', error);
    return false;
  }
};

/**
 * Count total messages stored in the database
 * @returns {number} The total count of messages
 */
export const getMessageCount = async () => {
  const db = await getDb();
  return db.count(STORES.MESSAGES);
};

/**
 * Clear all chat data (use with caution!)
 */
export const clearAllChatData = async () => {
  const db = await getDb();
  
  const tx1 = db.transaction(STORES.ROOMS, 'readwrite');
  await tx1.store.clear();
  await tx1.done;
  
  const tx2 = db.transaction(STORES.MESSAGES, 'readwrite');
  await tx2.store.clear();
  await tx2.done;
  
  const tx3 = db.transaction(STORES.OFFLINE_QUEUE, 'readwrite');
  await tx3.store.clear();
  await tx3.done;
};